import { Home, Users, Compass, Store, MessagesSquare, Bell, Plane, Music, Sparkles, Bookmark, Settings, ShieldCheck, Coins, Trophy, UtensilsCrossed } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { currentUser } from "@/lib/mockData";

const primaryItems = [
  { icon: Home,           label: "Inicio",        active: true,  testid: "nav-home", href: "/" },
  { icon: Users,          label: "Vínculos",      mode: "vinculos",  badge: 3, testid: "nav-vinculos" },
  { icon: Compass,        label: "Horizonte",     mode: "horizonte", testid: "nav-horizonte" },
  { icon: MessagesSquare, label: "Mensajes",      href: "/mensajes",        badge: 5,  testid: "nav-mensajes" },
  { icon: Bell,           label: "Notificaciones",href: "/notificaciones",  badge: 12, testid: "nav-notif" },
];

const exploreItems = [
  { icon: Trophy,   label: "Mi Perfil",       href: "/perfil",        testid: "nav-perfil" },
  { icon: Users,    label: "Comunidades",     href: "/comunidades",   testid: "nav-comunidades" },
  { icon: Store,    label: "Marketplace",     href: "/marketplace",   testid: "nav-marketplace" },
  { icon: Plane,    label: "Viajes",          href: "/viajes",        badge: "IA", testid: "nav-viajes" },
  { icon: UtensilsCrossed, label: "Restaurantes", href: "/restaurantes", testid: "nav-restaurantes" },
  { icon: Music,    label: "Obras Musicales", href: "/musica",        badge: "NEW", testid: "nav-musica" },
  { icon: Coins,    label: "Golpe de Gracia", href: "/referidos",     testid: "nav-referidos" },
  { icon: Sparkles, label: "Asistente IA",    href: "/perfil",        badge: "Pronto", testid: "nav-ai" },
  { icon: Bookmark, label: "Guardados",       href: "/guardados",     testid: "nav-saved" },
];

export default function Sidebar({ activeFeed, setActiveFeed }) {
  const loc = useLocation();
  return (
    <aside
      data-testid="sidebar"
      className="hidden md:flex md:w-[252px] lg:w-[280px] shrink-0 flex-col h-[calc(100vh-64px)] sticky top-16 border-r hairline bg-cream/80 backdrop-blur-sm"
    >
      <div className="flex-1 overflow-y-auto py-5 px-3">
        {/* User card */}
        <Link
          to="/perfil"
          data-testid="sidebar-profile"
          className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-cream-card transition-colors"
        >
          <img src={currentUser.avatar} alt="" className="w-10 h-10 rounded-full ring-2 ring-coral/40" />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-sm font-medium text-ink truncate">
              {currentUser.name}
              {currentUser.verified && <ShieldCheck className="w-3.5 h-3.5 text-coral shrink-0" />}
            </div>
            <div className="text-xs text-ink-mid truncate">Ver tu perfil</div>
          </div>
        </Link>

        <div className="mt-4 px-3 mb-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">Principal</p>
        </div>
        <nav className="flex flex-col gap-0.5">
          {primaryItems.map((it) => {
            const Icon = it.icon;
            const isHomeActive = it.href === "/" && loc.pathname === "/";
            const isFeedItem = Boolean(it.mode);
            const isActive = isHomeActive || (isFeedItem && activeFeed === it.mode && loc.pathname === "/");
            const Wrapper = it.href ? Link : "button";
            const wrapperProps = it.href ? { to: it.href } : {
              onClick: () => isFeedItem && setActiveFeed && setActiveFeed(it.mode),
            };
            return (
              <Wrapper
                key={it.label}
                data-testid={it.testid}
                {...wrapperProps}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors text-left ${
                  isActive
                    ? "bg-white text-ink shadow-sm border hairline"
                    : "text-ink/80 hover:bg-white"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" strokeWidth={1.6} />
                <span className="flex-1 truncate">{it.label}</span>
                {it.badge && (
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-coral text-cream">
                    {it.badge}
                  </span>
                )}
              </Wrapper>
            );
          })}
        </nav>

        <div className="mt-6 px-3 mb-2">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">Explorar</p>
        </div>
        <nav className="flex flex-col gap-0.5">
          {exploreItems.map((it) => {
            const Icon = it.icon;
            const isActive = it.href && loc.pathname === it.href;
            const Wrapper = it.href ? Link : "button";
            const wrapperProps = it.href ? { to: it.href } : {};
            return (
              <Wrapper
                key={it.label}
                data-testid={it.testid}
                {...wrapperProps}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors text-left ${
                  isActive ? "bg-white text-ink shadow-sm border hairline" : "text-ink/80 hover:bg-white"
                }`}
              >
                <Icon className="w-5 h-5 shrink-0" strokeWidth={1.6} />
                <span className="flex-1 truncate">{it.label}</span>
                {it.badge && (
                  <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${
                    it.badge === "NEW" ? "bg-coral text-cream" : "bg-ink text-cream"
                  }`}>
                    {it.badge}
                  </span>
                )}
              </Wrapper>
            );
          })}
        </nav>

        {/* Privacy footer card */}
        <div className="mt-8 mx-1 rounded-2xl border hairline bg-white p-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-coral" />
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">
              Privacidad por diseño
            </p>
          </div>
          <p className="mt-2 text-[13px] text-ink/75 leading-relaxed">
            Tu feed funciona sin algoritmos opacos. Decides tú qué ver.
          </p>
          <a href="/landing" data-testid="sidebar-manifest-link" className="mt-3 inline-flex items-center gap-1 text-xs text-coral link-underline">
            Leer manifiesto →
          </a>
        </div>
      </div>

      <div className="border-t hairline px-5 py-3 flex items-center justify-between">
        <button data-testid="sidebar-settings" className="flex items-center gap-2 text-xs text-ink-mid hover:text-ink transition">
          <Settings className="w-4 h-4" />
          Ajustes
        </button>
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-mid">v0.1</span>
      </div>
    </aside>
  );
}
